import type { MbcBriefQuality } from "@nlc/shared-types";

import { Check, Minus, X } from "lucide-react";

import { ProgressBar } from "@/components/progress-bar";
import { scoreColor } from "@/utils/scoreColor";

const BAND_LABELS: Record<MbcBriefQuality["band"], string> = {
  good: "Strong",
  medium: "Moderate",
  poor: "Needs work",
};

type BriefQualityCardProps = {
  quality: MbcBriefQuality | null;
};

/** Shared between the chat page's sidebar and the review page. The criterion,
 * its grade and the judge's suggestion arrive already joined — this only
 * renders. Nothing until the brief endpoint has answered once for the thread. */
export function BriefQualityCard({ quality }: BriefQualityCardProps) {
  if (!quality) return null;

  return (
    <section className="rounded-2xl border border-border-primary bg-bg-primary p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-text-primary">
        Brief quality score
      </h3>

      <div className="mt-4 flex items-end justify-between">
        <span
          className="text-sm font-semibold"
          style={{ color: scoreColor(quality.score) }}
        >
          {BAND_LABELS[quality.band]}
        </span>
        <span
          className="text-2xl font-bold"
          style={{ color: scoreColor(quality.score) }}
        >
          {quality.score}%
        </span>
      </div>
      <div className="mt-2">
        <ProgressBar
          fillColor={scoreColor(quality.score)}
          showValue={false}
          value={quality.score}
        />
      </div>

      <ul className="mt-5 flex flex-col gap-3">
        {quality.items.map((item) => (
          <li className="flex items-start gap-2" key={item.id}>
            {item.quality === null ? (
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-gray-50 text-utility-gray-700">
                <Minus className="size-3" strokeWidth={3} />
              </span>
            ) : item.quality === "good" ? (
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-success-50 text-utility-success-700">
                <Check className="size-3" strokeWidth={3} />
              </span>
            ) : (
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-error-50 text-utility-error-700">
                <X className="size-3" strokeWidth={3} />
              </span>
            )}
            <div>
              <p className="text-sm text-text-secondary">{item.label}</p>
              {item.suggestion ? (
                <p className="mt-0.5 text-xs text-text-tertiary">
                  {item.suggestion}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
